// Routing Intelligence v2 contracts (PRD "MaestrOS Routing Intelligence v2", decision D24): the typed Jev
// classification vocabulary, execution tiers, cache states, escalation reasons, the `routing_intelligence` section of
// the routing configuration, and the API DTOs of routing decisions, route affinity, model performance and the cache
// dashboard. Jev suggests; MaestrOS decides (D-RI-02): nothing here lets a classification pick a physical route.
import { z } from "zod";
import { RISK_LEVELS } from "./enums.ts";

const id = z.uuid();
const timestamp = z.iso.datetime();

export const ROUTING_POLICY_VERSION = "routing-intelligence-2" as const;
export const JEV_POLICY_V2_VERSION = "jev-policy-2" as const;
/** Versioned warm-cache value / cold-cache penalty formula (PRD §14). */
export const CACHE_VALUE_FORMULA_VERSION = "cache-value-1" as const;
/** D-RI-01: the only AI classifier of this version, through the OpenRouter Decisions API. */
export const JEV_MODEL_ID = "typesafe/jev-1.13" as const;
/**
 * Published input price of JEV_MODEL_ID in USD per million tokens (output is free), revalidated by the bootstrap
 * discovery. Used only to ESTIMATE a Jev call when the provider does not report its cost (never zero, D24 item 5).
 */
export const JEV_INPUT_USD_PER_MTOK = 0.042;
/** AC-RI-29: the Jev router may be studied later, never used as an execution route in this version. */
export const JEV_ROUTER_MODEL_PREFIX = "typesafe/jev-router" as const;

// ---------- Classification vocabulary (PRD §7) ----------

export const TASK_DOMAINS = ["frontend", "backend", "database", "devops", "security", "tests", "documentation", "research", "integration", "mixed"] as const;
export type TaskDomain = (typeof TASK_DOMAINS)[number];

export const TASK_KINDS = ["investigation", "implementation", "refactor", "bugfix", "test", "documentation", "review", "operation"] as const;
export type TaskKind = (typeof TASK_KINDS)[number];

export const COMPLEXITIES = ["trivial", "low", "medium", "high", "critical"] as const;
export type Complexity = (typeof COMPLEXITIES)[number];

export const CONTEXT_NEEDS = ["small", "medium", "large", "very_large"] as const;
export type ContextNeed = (typeof CONTEXT_NEEDS)[number];

export const REASONING_NEEDS = ["low", "medium", "high", "frontier"] as const;
export type ReasoningNeed = (typeof REASONING_NEEDS)[number];

/** T0 deterministic (no model) → T1 economy → T2 premium (D-RI-07). */
export const MODEL_TIERS = ["deterministic", "economy", "premium"] as const;
export type ModelTier = (typeof MODEL_TIERS)[number];

export const JEV_CHOICE_DIMENSIONS = ["domain", "task_kind", "complexity", "context_need", "reasoning_need", "model_tier"] as const;
export const JEV_BOOLEAN_DIMENSIONS = ["splittable", "parallelizable", "needs_browser", "needs_external_research", "needs_security_review"] as const;
export type JevDimensionKey = (typeof JEV_CHOICE_DIMENSIONS)[number] | (typeof JEV_BOOLEAN_DIMENSIONS)[number];

export const taskDimensionsSchema = z
  .object({
    domain: z.enum(TASK_DOMAINS),
    task_kind: z.enum(TASK_KINDS),
    complexity: z.enum(COMPLEXITIES),
    context_need: z.enum(CONTEXT_NEEDS),
    reasoning_need: z.enum(REASONING_NEEDS),
    model_tier: z.enum(MODEL_TIERS),
    splittable: z.boolean(),
    parallelizable: z.boolean(),
    needs_browser: z.boolean(),
    needs_external_research: z.boolean(),
    needs_security_review: z.boolean(),
  })
  .strict();
export type TaskDimensions = z.infer<typeof taskDimensionsSchema>;

/**
 * Where the effective classification came from: `rules` (unambiguous, no model), `jev` (confidence ≥ auto-apply),
 * `jev_rules` (Jev combined with the rules), `conservative` (low confidence), `degraded` (Jev failed; rules).
 */
export const CLASSIFICATION_SOURCES = ["rules", "jev", "jev_rules", "conservative", "degraded"] as const;
export type ClassificationSource = (typeof CLASSIFICATION_SOURCES)[number];

export const CONFIDENCE_BINS = ["lt_0_70", "0_70_to_0_90", "gte_0_90"] as const;
export type ConfidenceBin = (typeof CONFIDENCE_BINS)[number];

/** What MaestrOS sends to Jev (PRD §35): never secrets, credentials, `.env` or whole files. */
export interface JevInputV2 {
  taskId: string;
  title: string;
  /** Redacted and bounded (≤ 4000 characters). */
  description: string;
  type: string;
  risk: string;
  labels: string[];
  /** Redacted and bounded (≤ 2000 characters). */
  requirementSummary: string;
  inputHash: string;
  policyVersion: string;
}

/** Typed Jev answer after validation (no free-text parsing, AC-RI-04). */
export const jevClassificationSchema = z.object({
  dimensions: taskDimensionsSchema,
  /** Per-question confidence in 0..1 (booleans: max(p, 1 − p) of the probability of true). */
  confidences: z.partialRecord(z.enum([...JEV_CHOICE_DIMENSIONS, ...JEV_BOOLEAN_DIMENSIONS]), z.number().min(0).max(1)),
  /** Overall confidence: the lowest per-question confidence. */
  confidence: z.number().min(0).max(1),
  /** Model that answered (AC-RI-02). */
  model: z.string().min(1).max(200),
  provider: z.string().max(100).nullable(),
  usage: z.object({
    input_tokens: z.number().int().nonnegative().nullable(),
    output_tokens: z.number().int().nonnegative().nullable(),
    /** USD as reported by the provider; null = unknown (never zero). */
    cost_usd: z.number().nonnegative().nullable(),
  }),
  latency_ms: z.number().int().nonnegative(),
  retries: z.number().int().nonnegative(),
});
export type JevClassification = z.infer<typeof jevClassificationSchema>;

/** Engine port of the Jev classifier v2 (OpenRouter Decisions API; a labeled fake in tests). */
export interface JevClassifierPort {
  readonly model: string;
  classify(input: JevInputV2, signal: AbortSignal): Promise<JevClassification>;
}

// ---------- Escalation and cache (PRD §13, §19) ----------

export const ESCALATION_REASONS = [
  "quality_gate_failed",
  "critic_rejected",
  "repeated_test_failure",
  "no_progress",
  "context_capacity",
  "capability_missing",
  "provider_unavailable",
  "quota_exhausted",
  "high_risk_policy",
  "human_override",
] as const;
export type EscalationReason = (typeof ESCALATION_REASONS)[number];

export const CACHE_STATES = ["unknown", "cold", "warming", "warm", "lost"] as const;
export type CacheState = (typeof CACHE_STATES)[number];

/** How the attempt's session affinity is carried: the OpenRouter `session_id`, unsupported by the path, or off. */
export const SESSION_AFFINITY_MODES = ["openrouter_session_id", "unsupported", "disabled"] as const;
export type SessionAffinityMode = (typeof SESSION_AFFINITY_MODES)[number];

export const REASONING_EFFORTS = ["low", "medium", "high"] as const;
export type ReasoningEffort = (typeof REASONING_EFFORTS)[number];

// ---------- Configuration section (feature flags and thresholds, AC-RI-35) ----------

export const routingIntelligenceConfigSchema = z.object({
  /** ROUTING_INTELLIGENCE_V2: tiered, cache-aware routing decisions for new builder attempts. */
  enabled: z.boolean().default(false),
  /** JEV_OPENROUTER_ENABLED: ambiguous tasks are classified by Jev through OpenRouter. */
  jev_openrouter: z.boolean().default(false),
  /** DEEPSEEK_V4_PILOT: paid economy targets may run builder attempts under the economy caps. */
  deepseek_pilot: z.boolean().default(false),
  /** CACHE_AWARE_SCORING: warm-cache value and cold-cache penalty enter the route score. */
  cache_aware_scoring: z.boolean().default(false),
  /** OPENROUTER_SESSION_AFFINITY: every OpenRouter call of an attempt carries its `session_id`. */
  session_affinity: z.boolean().default(false),
  /** ADAPTIVE_MODEL_SCORING: historical success per task class × model × provider enters the score. */
  adaptive_scoring: z.boolean().default(false),
  /** Share of tasks (deterministic bucket of the task id) that follow the v2 routing (RI-7). */
  rollout_percent: z.number().int().min(0).max(100).default(0),
  /** OpenRouter provider connection used for Jev (key in the vault). */
  jev_connection_id: id.nullable().default(null),
  /** T1 candidates, in preference order (free targets allowed: free-first is preserved as cost 0). */
  economy_model_target_ids: z.array(id).max(10).default([]),
  /** T2 candidates; empty = the Maestro target and its substitutes. */
  premium_model_target_ids: z.array(id).max(10).default([]),
  /** Ceiling of the paid economy spend per attempt, in cents. */
  economy_max_cost_cents_per_attempt: z.number().nonnegative().default(200),
  jev_thresholds: z
    .object({ auto_apply: z.number().min(0).max(1).default(0.9), combine: z.number().min(0).max(1).default(0.7) })
    .default({ auto_apply: 0.9, combine: 0.7 }),
  /** Share of the usable window (PRD §17). */
  compaction: z
    .object({ warning: z.number().min(0).max(1).default(0.5), checkpoint: z.number().min(0).max(1).default(0.7), compact: z.number().min(0).max(1).default(0.8) })
    .default({ warning: 0.5, checkpoint: 0.7, compact: 0.8 }),
  circuit_breaker: z
    .object({
      consecutive_failures: z.number().int().min(1).max(50).default(3),
      quality_failure_rate: z.number().min(0).max(1).default(0.6),
      min_attempts: z.number().int().min(1).max(1000).default(5),
      window_hours: z.number().int().min(1).max(720).default(24),
      cooldown_minutes: z.number().int().min(1).max(10_080).default(60),
    })
    .default({ consecutive_failures: 3, quality_failure_rate: 0.6, min_attempts: 5, window_hours: 24, cooldown_minutes: 60 }),
  /** PRD §28: hypothesis to calibrate, not a statistical guarantee. */
  promotion: z
    .object({
      min_evaluations: z.number().int().min(1).default(30),
      max_success_gap_pp: z.number().min(0).max(100).default(5),
      max_cost_ratio: z.number().min(0).default(0.4),
      max_cycle_ratio: z.number().min(0).default(1.5),
    })
    .default({ min_evaluations: 30, max_success_gap_pp: 5, max_cost_ratio: 0.4, max_cycle_ratio: 1.5 }),
});
export type RoutingIntelligenceConfig = z.infer<typeof routingIntelligenceConfigSchema>;
export const DEFAULT_ROUTING_INTELLIGENCE: RoutingIntelligenceConfig = routingIntelligenceConfigSchema.parse({});

// ---------- API DTOs (PRD §32) ----------

export const rejectedCandidateSchema = z.object({
  model_target_id: id.nullable(),
  label: z.string().max(300).nullable(),
  tier: z.enum(MODEL_TIERS).nullable(),
  reason: z.string().max(500),
});
export type RejectedCandidate = z.infer<typeof rejectedCandidateSchema>;

export const routeAffinitySchema = z.object({
  attempt_id: id,
  routing_session_id: z.string().max(256),
  session_affinity: z.enum(SESSION_AFFINITY_MODES),
  model_target_id: id.nullable(),
  effective_model: z.string().nullable(),
  connection_id: id.nullable(),
  effective_provider: z.string().nullable(),
  state: z.enum(CACHE_STATES),
  established_at: timestamp,
  last_seen_at: timestamp.nullable(),
  calls: z.number().int().nonnegative(),
  fresh_input_tokens: z.number().int().nonnegative(),
  cache_read_tokens: z.number().int().nonnegative(),
  cache_write_tokens: z.number().int().nonnegative(),
  output_tokens: z.number().int().nonnegative(),
  reasoning_tokens: z.number().int().nonnegative(),
  /** cache_read / (cache_read + fresh_input); null without a valid denominator. */
  cache_hit_ratio: z.number().min(0).max(1).nullable(),
  provider_changes: z.number().int().nonnegative(),
  cold_starts: z.number().int().nonnegative(),
  prefix_changes: z.number().int().nonnegative(),
});
export type RouteAffinity = z.infer<typeof routeAffinitySchema>;

export const routingDecisionSchema = z.object({
  id,
  project_id: id,
  task_id: id,
  attempt_id: id.nullable(),
  task_run_id: id.nullable(),
  policy_version: z.string(),
  source: z.enum(CLASSIFICATION_SOURCES),
  jev_model: z.string().nullable(),
  jev_confidence: z.number().min(0).max(1).nullable(),
  jev_dimensions: z.record(z.string(), z.unknown()).nullable(),
  /** `<domain>/<task_kind>/<complexity>` of the effective classification. */
  task_class: z.string(),
  specialty: z.string().nullable(),
  risk: z.enum(RISK_LEVELS),
  requested_tier: z.enum(MODEL_TIERS),
  applied_tier: z.enum(MODEL_TIERS),
  reasoning_effort: z.enum(REASONING_EFFORTS).nullable(),
  agent_profile_id: id.nullable(),
  agent_profile_version_id: id.nullable(),
  agent_name: z.string().nullable(),
  model_target_id: id.nullable(),
  model_display: z.string().nullable(),
  external_model_id: z.string().nullable(),
  connection_id: id.nullable(),
  routing_session_id_hash: z.string().nullable(),
  session_affinity: z.enum(SESSION_AFFINITY_MODES),
  cache_state_at_selection: z.enum(CACHE_STATES),
  warm_cache_value_estimated_cents: z.number().nullable(),
  cold_cache_penalty_estimated_cents: z.number().nullable(),
  escalation_reason: z.enum(ESCALATION_REASONS).nullable(),
  previous_attempt_id: id.nullable(),
  rollout_bucket: z.number().int().min(0).max(99).nullable(),
  premium_review_required: z.boolean(),
  decision_reason: z.string(),
  rejected_candidates: z.array(rejectedCandidateSchema),
  affinity: routeAffinitySchema.nullable(),
  created_at: timestamp,
});
export type RoutingDecision = z.infer<typeof routingDecisionSchema>;

export const PERFORMANCE_GROUP_KEYS = ["task_class", "specialty", "risk", "complexity", "agent_version", "model", "provider", "reasoning_effort", "tier"] as const;
export type PerformanceGroupKey = (typeof PERFORMANCE_GROUP_KEYS)[number];

export const modelPerformanceRowSchema = z.object({
  group: z.partialRecord(z.enum(PERFORMANCE_GROUP_KEYS), z.string().nullable()),
  attempts: z.number().int().nonnegative(),
  tasks: z.number().int().nonnegative(),
  accepted: z.number().int().nonnegative(),
  first_pass_accepted: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  escalated: z.number().int().nonnegative(),
  /** Attempts ended by quota waits or provider outages: never counted as quality failures (AC-RI-24/25). */
  infrastructure_interruptions: z.number().int().nonnegative(),
  avg_cost_cents: z.number().nullable(),
  median_cost_cents: z.number().nullable(),
  cost_per_accepted_task_cents: z.number().nullable(),
  /** Attempts whose cost is unknown: totals are partial when > 0. */
  unknown_cost_attempts: z.number().int().nonnegative(),
  avg_latency_ms: z.number().nullable(),
  median_duration_ms: z.number().nullable(),
  cache_hit_ratio: z.number().min(0).max(1).nullable(),
  rework_rate: z.number().min(0).max(1).nullable(),
  premium_escalation_rate: z.number().min(0).max(1).nullable(),
  test_pass_rate: z.number().min(0).max(1).nullable(),
});
export type ModelPerformanceRow = z.infer<typeof modelPerformanceRowSchema>;

export const promotionVerdictSchema = z.object({
  task_class: z.string(),
  economy_model: z.string(),
  premium_model: z.string().nullable(),
  economy_evaluations: z.number().int().nonnegative(),
  premium_evaluations: z.number().int().nonnegative(),
  verdict: z.enum(["promote", "hold", "insufficient_data"]),
  reasons: z.array(z.string()),
  success_gap_pp: z.number().nullable(),
  cost_ratio: z.number().nullable(),
  cycle_ratio: z.number().nullable(),
  critical_regressions_delta: z.number().nullable(),
});
export type PromotionVerdict = z.infer<typeof promotionVerdictSchema>;

export const cacheSummarySchema = z.object({
  attempts: z.number().int().nonnegative(),
  calls: z.number().int().nonnegative(),
  fresh_input_tokens: z.number().int().nonnegative(),
  cache_read_tokens: z.number().int().nonnegative(),
  cache_write_tokens: z.number().int().nonnegative(),
  hit_ratio: z.number().min(0).max(1).nullable(),
  /** Σ cache_read × (input price − cache read price); null when a price is unknown (never zero). */
  estimated_savings_cents: z.number().nullable(),
  savings_coverage: z.object({ known: z.number().int().nonnegative(), total: z.number().int().nonnegative() }),
  cold_starts: z.number().int().nonnegative(),
  provider_changes: z.number().int().nonnegative(),
  prefix_changes: z.number().int().nonnegative(),
  by_state: z.partialRecord(z.enum(CACHE_STATES), z.number().int().nonnegative()),
});
export type CacheSummary = z.infer<typeof cacheSummarySchema>;

export const modelPerformanceResponseSchema = z.object({
  project_id: id.nullable(),
  since: timestamp,
  group_by: z.array(z.enum(PERFORMANCE_GROUP_KEYS)),
  rows: z.array(modelPerformanceRowSchema),
  promotion: z.array(promotionVerdictSchema),
  criteria: routingIntelligenceConfigSchema.shape.promotion,
  cache: cacheSummarySchema,
  /**
   * D28: builder attempts aborted by a model divergence (a router alias answering with an unauthorized model) — a route
   * configuration error, not a model's quality — kept out of `rows` and `promotion` and counted here.
   */
  excluded_configuration_attempts: z.number().int().nonnegative().default(0),
});
export type ModelPerformanceResponse = z.infer<typeof modelPerformanceResponseSchema>;

export const modelPerformanceQuerySchema = z.object({
  days: z.coerce.number().int().min(1).max(365).default(7),
  group_by: z
    .string()
    .max(200)
    .optional()
    .transform((v, ctx) => {
      const keys = (v ?? "task_class,model,provider").split(",").map((s) => s.trim()).filter((s) => s !== "");
      const bad = keys.filter((k) => !(PERFORMANCE_GROUP_KEYS as readonly string[]).includes(k));
      if (bad.length > 0 || keys.length === 0) {
        ctx.addIssue({ code: "custom", message: `group_by accepts ${PERFORMANCE_GROUP_KEYS.join(", ")}` });
        return z.NEVER;
      }
      return [...new Set(keys)] as PerformanceGroupKey[];
    }),
});

export const jevStatusSchema = z.object({
  model: z.string(),
  policy_version: z.string(),
  flags: z.object({ enabled: z.boolean(), jev_openrouter: z.boolean() }),
  thresholds: routingIntelligenceConfigSchema.shape.jev_thresholds,
  connection: z.object({ id, name: z.string(), adapter: z.string(), state: z.string(), secret_state: z.string() }).nullable(),
  /** The connection is allowlisted for real calls on this API process (fail-closed). */
  real_calls_allowed: z.boolean(),
  ready: z.boolean(),
  impediments: z.array(z.string()),
  window: z.object({
    since: timestamp,
    jev: z.number().int().nonnegative(),
    rules: z.number().int().nonnegative(),
    degraded: z.number().int().nonnegative(),
    avg_latency_ms: z.number().nullable(),
    cost_usd: z.number().nullable(),
  }),
  last_degraded: z.object({ at: timestamp, reason: z.string().nullable() }).nullable(),
  /** PRD §8: confidence bin × real outcome of the attempts routed with that classification. */
  calibration: z.array(z.object({ bin: z.enum(CONFIDENCE_BINS), classifications: z.number().int(), accepted: z.number().int(), failed: z.number().int() })),
});
export type JevStatus = z.infer<typeof jevStatusSchema>;

export const jevTestRequestSchema = z.object({
  title: z.string().trim().min(1).max(300),
  description: z.string().max(4000).default(""),
  type: z.string().trim().min(1).max(40).default("feature"),
  risk: z.enum(RISK_LEVELS).default("medium"),
  labels: z.array(z.string().max(60)).max(20).default([]),
  /** Real calls cost money: an explicit ceiling is required (one Jev call costs a fraction of a cent). */
  max_cost_cents: z.number().positive().max(100),
});
